import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router';
import { Loader2, Pause, Play, RefreshCw, Save, Trash2 } from 'lucide-react';
import { useHeaderAction } from '@/lib/header-action-context';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { TriggerEditor } from '@/components/admin/scheduler/TriggerEditor';
import { schedulerApi, type ScheduledTrigger, type ScheduledTriggerRun, type TriggerCreateInput } from '@/lib/scheduler-api';

const HEADER_LAYER_ID = 'trigger-page';

export default function TriggerDetailPage() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();

    const [trigger, setTrigger] = useState<ScheduledTrigger | null>(null);
    const [runs, setRuns] = useState<ScheduledTriggerRun[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const { setHeaderTitle, pushHeaderLayer, removeHeaderLayer } = useHeaderAction();

    useEffect(() => {
        if (!id) return;
        (async () => {
            setLoading(true);
            try {
                const [trigRes, runsRes] = await Promise.all([
                    schedulerApi.getTrigger(id),
                    schedulerApi.listRuns(id, 20),
                ]);
                setTrigger(trigRes.data.data);
                setRuns(runsRes.data.data?.runs ?? []);
            } catch { /* ignore */ }
            setLoading(false);
        })();
    }, [id]);

    useEffect(() => {
        if (!trigger?.name) {
            setHeaderTitle(null);
            return;
        }
        setHeaderTitle({
            title: trigger.name,
            badges: (
                <span
                    className={`rounded-full px-2 py-0.5 text-[10px] font-medium uppercase ${
                        trigger.enabled ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'
                    }`}
                >
                    {trigger.enabled ? 'Enabled' : 'Paused'}
                </span>
            ),
        });
        return () => setHeaderTitle(null);
    }, [trigger?.name, trigger?.enabled, setHeaderTitle]);

    const handleSave = async (input: TriggerCreateInput) => {
        if (!id) return;
        setSaving(true);
        try {
            await schedulerApi.updateTrigger(id, input);
            navigate('/scheduler/triggers');
        } catch { /* ignore */ }
        setSaving(false);
    };

    const handleToggle = async () => {
        if (!trigger || !id) return;
        try {
            if (trigger.enabled) {
                await schedulerApi.pauseTrigger(id);
            } else {
                await schedulerApi.resumeTrigger(id);
            }
            const res = await schedulerApi.getTrigger(id);
            setTrigger(res.data.data);
        } catch { /* ignore */ }
    };

    const handleFire = async () => {
        if (!id) return;
        try {
            await schedulerApi.fireTrigger(id);
            const runsRes = await schedulerApi.listRuns(id, 20);
            setRuns(runsRes.data.data?.runs ?? []);
        } catch { /* ignore */ }
    };

    const handleDelete = async () => {
        if (!id || !confirm('Delete this trigger? This cannot be undone.')) return;
        try {
            await schedulerApi.deleteTrigger(id);
            navigate('/scheduler/triggers');
        } catch { /* ignore */ }
    };

    const toggleRef = useRef(handleToggle);
    toggleRef.current = handleToggle;
    const fireRef = useRef(handleFire);
    fireRef.current = handleFire;
    const deleteRef = useRef(handleDelete);
    deleteRef.current = handleDelete;

    useEffect(() => {
        if (!trigger) {
            removeHeaderLayer(HEADER_LAYER_ID);
            return;
        }
        pushHeaderLayer(HEADER_LAYER_ID, {
            action: (
                <div className="flex items-center gap-2">
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => void toggleRef.current()}
                        className="inline-flex items-center gap-1.5"
                    >
                        {trigger.enabled ? (
                            <><Pause size={14} /> Pause</>
                        ) : (
                            <><Play size={14} /> Resume</>
                        )}
                    </Button>
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => void fireRef.current()}
                        className="inline-flex items-center gap-1.5"
                    >
                        <RefreshCw size={14} />
                        Run Now
                    </Button>
                    <Button
                        type="button"
                        variant="default"
                        size="sm"
                        onClick={() => {
                            const form = document.querySelector<HTMLFormElement>('#trigger-editor-form');
                            if (form) form.requestSubmit();
                        }}
                        disabled={saving}
                        className="inline-flex items-center gap-1.5"
                    >
                        {saving ? (
                            <Loader2 size={14} className="animate-spin" />
                        ) : (
                            <Save size={14} />
                        )}
                        Save
                    </Button>
                    <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        onClick={() => void deleteRef.current()}
                        className="inline-flex items-center gap-1.5"
                    >
                        <Trash2 size={14} />
                        Delete
                    </Button>
                </div>
            ),
        });
        return () => removeHeaderLayer(HEADER_LAYER_ID);
    }, [trigger, saving, pushHeaderLayer, removeHeaderLayer]);

    if (loading) {
        return (
            <div className="flex items-center justify-center p-12">
                <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
            </div>
        );
    }

    if (!trigger) {
        return (
            <div className="p-6">
                <p className="text-slate-600">Trigger not found.</p>
            </div>
        );
    }

    return (
        <div className="p-12 pt-2 space-y-2">
            <div className="px-10">
                <Card className="border-white/30 bg-white/40 p-6 shadow-sm">
                    <TriggerEditor
                        initial={{
                            name: trigger.name,
                            triggerType: trigger.triggerType,
                            scheduleKind: trigger.scheduleKind,
                            targetRef: trigger.targetRef,
                            targetMethod: trigger.targetMethod,
                            cronExpression: trigger.cronExpression,
                            rruleExpression: trigger.rruleExpression,
                            timezone: trigger.timezone,
                            concurrencyPolicy: trigger.concurrencyPolicy,
                            misfirePolicy: trigger.misfirePolicy,
                            delayUntilAt: trigger.delayUntilAt,
                            inputPayload: trigger.inputPayload,
                        }}
                        onSave={handleSave}
                        saving={saving}
                    />

                    {runs.length > 0 && (
                        <div className="mt-6">
                            <CardHeader className="px-0">
                                <CardTitle className="text-sm font-medium">Recent Runs</CardTitle>
                            </CardHeader>
                            <CardContent className="p-0">
                                <div className="overflow-x-auto">
                                    <table className="w-full text-sm">
                                        <thead>
                                            <tr className="border-b bg-muted/50">
                                                <th className="text-left p-3 font-medium">Scheduled For</th>
                                                <th className="text-left p-3 font-medium">Status</th>
                                                <th className="text-left p-3 font-medium">Started</th>
                                                <th className="text-left p-3 font-medium">Completed</th>
                                                <th className="text-left p-3 font-medium">Attempt</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {runs.map((r) => (
                                                <tr key={r.id} className="border-b">
                                                    <td className="p-3 text-xs">
                                                        {new Date(r.scheduledForAt).toLocaleString()}
                                                    </td>
                                                    <td className="p-3">
                                                        <Badge variant={
                                                            r.status === 'succeeded' ? 'default' :
                                                            r.status === 'failed' ? 'destructive' :
                                                            'secondary'
                                                        }>
                                                            {r.status}
                                                        </Badge>
                                                    </td>
                                                    <td className="p-3 text-xs">
                                                        {r.startedAt ? new Date(r.startedAt).toLocaleString() : '—'}
                                                    </td>
                                                    <td className="p-3 text-xs">
                                                        {r.completedAt ? new Date(r.completedAt).toLocaleString() : '—'}
                                                    </td>
                                                    <td className="p-3 text-center">{r.attempt}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </CardContent>
                        </div>
                    )}
                </Card>
            </div>
        </div>
    );
}
